/**
 * useBrandingInit
 *
 * Fetches branding data from the API on app startup (once the user is
 * authenticated) and applies the persisted customisations immediately:
 *   - Navbar colour  → CSS custom property `--header-primary`
 *   - Browser tab title → `document.title`
 *   - Favicon        → `<link rel="icon">`
 *   - Nav logos      → Redux logoSlice (for the sidebar)
 *
 * This hook is intentionally lightweight – it only reads and applies; it
 * does NOT manage the full branding-editor state (that remains in BrandingTab).
 */

import { useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { getBranding } from '@/services/apis/api'
import { useAppDispatch } from '@/hooks/storeHooks/storehooks'
import { setLogoData } from '@/redux-store/slices/logoSlice/logoSlice'
import type {
  BrandingApiResponse,
  BrandingData,
} from '@/designs/new/containers/Settings/branding/brandingTypes'

// ── Helpers (mirrors the ones in BrandingTab) ─────────────────────────────

function applyFavicon(url: string) {
  if (!url) return
  const existing =
    document.querySelectorAll<HTMLLinkElement>("link[rel*='icon']")
  existing.forEach(l => l.remove())
  const link = document.createElement('link')
  link.rel = 'icon'
  link.href = url
  document.head.appendChild(link)
}

// ── Hook ──────────────────────────────────────────────────────────────────

export function useBrandingInit() {
  const { user, isAuthenticated } = useAuth0()
  const dispatch = useAppDispatch()

  const orgId: string =
    (user as { org_id?: string; organizationId?: string } | undefined)
      ?.org_id ??
    (user as { org_id?: string; organizationId?: string } | undefined)
      ?.organizationId ??
    ''

  useEffect(() => {
    if (!isAuthenticated || !orgId) return

    // Fire-and-forget: this hook is purely additive.
    // Any failure must NOT affect the rest of the app.
    const applyBranding = async () => {
      try {
        const res = await getBranding(orgId)

        const resPayload = res as BrandingApiResponse | undefined
        const resData = resPayload?.data
        const d: BrandingData | undefined =
          resData?.data && typeof resData.data === 'object'
            ? resData.data
            : resData

        // No branding configured yet — nothing to apply, leave app defaults intact
        if (!d) return

        // ── Navbar colour ────────────────────────────────────────────────
        try {
          const navbarColor = d.navbarColor ?? d.navbar_color
          if (navbarColor) {
            document.documentElement.style.setProperty(
              '--header-primary',
              navbarColor,
              'important',
            )
          }
        } catch {
          /* non-critical, ignore */
        }

        // ── Browser tab title ────────────────────────────────────────────
        try {
          const tabTitle = d.tabTitle ?? d.tab_title
          if (tabTitle) {
            document.title = tabTitle
          }
        } catch {
          /* non-critical, ignore */
        }

        // ── Favicon ──────────────────────────────────────────────────────
        try {
          const faviconUrl = d.faviconUrl ?? d.favicon_url ?? d.favicon
          if (faviconUrl) {
            applyFavicon(faviconUrl)
          }
        } catch {
          /* non-critical, ignore */
        }

        // ── Sidebar logos (Redux) ────────────────────────────────────────
        try {
          const lightLogo =
            d.navLogoLightUrl ?? d.nav_logo_light_url ?? d.navLogoLight ?? ''
          const darkLogo =
            d.navLogoDarkUrl ?? d.nav_logo_dark_url ?? d.navLogoDark ?? ''
          const miniLight =
            d.collapsedMenuIconLightUrl ??
            d.collapsed_menu_icon_light_url ??
            d.collapsedMenuIconLight ??
            ''
          const miniDark =
            d.collapsedMenuIconDarkUrl ??
            d.collapsed_menu_icon_dark_url ??
            d.collapsedMenuIconDark ??
            ''

          // Only dispatch if there are actual logos — don't overwrite existing Redux state otherwise
          if (lightLogo || darkLogo || miniLight || miniDark) {
            const effectiveLight = lightLogo || ''
            const effectiveDark = darkLogo || effectiveLight
            const effectiveMiniLight = miniLight || effectiveLight
            const effectiveMiniDark =
              miniDark || effectiveDark || effectiveMiniLight || effectiveLight

            dispatch(
              setLogoData({
                logoShow: true,
                images: {
                  logoLargeLight: effectiveLight,
                  logoLargeDark: effectiveDark,
                  logoMiniLight: effectiveMiniLight,
                  logoMiniDark: effectiveMiniDark,
                },
              }),
            )
          }
        } catch {
          /* non-critical, ignore */
        }
      } catch (err) {
        // getBranding failed (network error, 4xx, 5xx, etc.)
        // Log silently — the app continues with its defaults, no crash.
        console.warn(
          '[useBrandingInit] Could not load branding, using defaults:',
          err,
        )
      }
    }

    applyBranding()
  }, [isAuthenticated, orgId, dispatch])
}
